import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { Theme } from './types';
import { ThemeManager } from './ThemeManager';

interface ThemeContextType {
  theme: Theme;
  themes: Theme[];
  setTheme: (themeId: string) => void;
  createCustomTheme: (baseThemeId: string, overrides: Partial<Theme>) => Theme | null;
  registerTheme: (theme: Theme) => void;
  exportTheme: () => string;
  importTheme: (themeJson: string) => boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  initialTheme?: string;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children, initialTheme }) => {
  const manager = ThemeManager.getInstance();
  const [theme, setThemeState] = useState<Theme>(manager.getCurrentTheme());
  const [themes, setThemes] = useState<Theme[]>(manager.getThemes());
  
  useEffect(() => {
    // Apply initial theme if provided
    if (initialTheme) {
      manager.applyTheme(initialTheme);
      setThemeState(manager.getCurrentTheme());
    }
    
    // Listen for theme changes
    const handleThemeChange = (event: CustomEvent) => {
      setThemeState(event.detail);
    };
    
    window.addEventListener('themechange', handleThemeChange as EventListener);
    
    return () => {
      window.removeEventListener('themechange', handleThemeChange as EventListener);
    };
  }, [initialTheme, manager]);
  
  const setTheme = (themeId: string) => {
    if (manager.applyTheme(themeId)) {
      setThemeState(manager.getCurrentTheme());
    }
  };
  
  const createCustomTheme = (baseThemeId: string, overrides: Partial<Theme>) => {
    const customTheme = manager.createCustomTheme(baseThemeId, overrides);
    if (customTheme) {
      manager.registerTheme(customTheme);
      setThemes(manager.getThemes());
    }
    return customTheme;
  };
  
  const registerTheme = (theme: Theme) => {
    manager.registerTheme(theme);
    setThemes(manager.getThemes());
  };
  
  const exportTheme = () => {
    return manager.exportTheme();
  };
  
  const importTheme = (themeJson: string) => {
    const success = manager.importTheme(themeJson);
    if (success) {
      setThemes(manager.getThemes());
    }
    return success;
  };
  
  return (
    <ThemeContext.Provider
      value={{
        theme,
        themes,
        setTheme,
        createCustomTheme,
        registerTheme,
        exportTheme,
        importTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};